import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Spin } from "antd";
import { ShareAltOutlined } from "@ant-design/icons";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import ConsentPanel from "../../components/delivery/ConsentPanel";
import ExpiredPanel from "../../components/delivery/ExpiredPanel";
import ErrorPanel from "../../components/delivery/ErrorPanel";
import DriveEmbed from "../../components/delivery/DriveEmbed";
import ExpiryCountdown from "../../components/delivery/ExpiryCountdown";
import {
    getConsentClauseIds,
    hasConsent,
    isExpired,
    setConsent,
} from "../../utils/deliveryToken";
import { useDeliveryPayload } from "../../hooks/useDeliveryPayload";
import { useTranslation } from "../../i18n";
import "./delivery.css";

function Delivery() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { status, payload, reload } = useDeliveryPayload(token);
    const [consented, setConsented] = useState(false);
    const [expired, setExpired] = useState(false);

    useEffect(() => {
        if (!payload) {
            setConsented(false);
            setExpired(false);
            return;
        }
        setExpired(isExpired(payload.e));
        setConsented(getConsentClauseIds(payload).length === 0 || hasConsent(token));
    }, [payload, token]);

    useEffect(() => {
        const brand = "HEI CREATIVE";
        if (status === "loading") {
            document.title = `${t("delivery.actions.loadingAlbum")} · ${brand}`;
        } else if (status === "expired") {
            document.title = `${t("delivery.meta.expiredLink")} · ${brand}`;
        } else if (!payload) {
            document.title = `${t("delivery.meta.invalidLink")} · ${brand}`;
        } else if (expired) {
            document.title = `${t("delivery.meta.expiredLink")} · ${brand}`;
        } else if (consented) {
            document.title = `${t("delivery.meta.gallery")} · ${brand}`;
        } else {
            document.title = `${t("delivery.meta.consent")} · ${brand}`;
        }
    }, [status, payload, expired, consented, t]);

    const handleAgree = useCallback(() => {
        setConsent(token);
        setConsented(true);
    }, [token]);

    const handleLeave = useCallback(() => {
        navigate("/");
    }, [navigate]);

    const handleExpired = useCallback(() => {
        setExpired(true);
        setConsented(false);
    }, []);

    const handleShare = useCallback(() => {
        navigate(`/d/${token}/share`);
    }, [navigate, token]);

    if (status === "loading") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <div className="delivery-browser delivery-browser--centered">
                        <Spin size="large" />
                        <p>{t("delivery.actions.loadingAlbum")}</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (status === "expired") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <ExpiredPanel archiveMarkdown={payload?.archiveMarkdown} />
                </div>
                <Footer />
            </>
        );
    }

    if (status === "error") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <div className="delivery-browser delivery-browser--centered">
                        <p className="delivery-album-error__text">
                            {t("delivery.actions.albumLoadFailed")}
                        </p>
                        <div className="delivery-album-error__actions">
                            <Button type="primary" onClick={() => navigate("/contact")}>
                                {t("delivery.actions.contactPhotographer")}
                            </Button>
                            <Button onClick={reload}>{t("delivery.actions.retry")}</Button>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!payload) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <ErrorPanel />
                </div>
                <Footer />
            </>
        );
    }

    if (expired || isExpired(payload.e)) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <ExpiredPanel archiveMarkdown={payload?.archiveMarkdown} />
                </div>
                <Footer />
            </>
        );
    }

    if (consented) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page delivery-page--gallery">
                    <div className="delivery-gallery-bar">
                        <ExpiryCountdown
                            key={token}
                            expiryUtcSeconds={payload.e}
                            onExpired={handleExpired}
                            compact
                        />
                        <Button
                            type="text"
                            className="delivery-gallery-bar__share"
                            icon={<ShareAltOutlined />}
                            onClick={handleShare}
                        >
                            {t("delivery.share.shareButton")}
                        </Button>
                    </div>
                    <DriveEmbed key={token} resource={payload} linkId={token} />
                </div>
            </>
        );
    }

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <div className="delivery-page">
                <ConsentPanel
                    key={token}
                    expiryUtcSeconds={payload.e}
                    clauseIds={getConsentClauseIds(payload)}
                    onAgree={handleAgree}
                    onLeave={handleLeave}
                    onExpired={handleExpired}
                />
            </div>
            <Footer />
        </>
    );
}

export default Delivery;
